/* eslint-disable */
import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./contexts/AuthContext";

import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import Terms from "./pages/legal/Terms";
import DataProtection from "./pages/legal/DataProtection";

import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import { setupNativeStatusBar } from "./utils/nativeStatusBar";

import ParentOnboarding from "./pages/ParentOnboarding";
import ParentSplash from "./pages/ParentSplash";
import ParentWelcome from "./pages/ParentWelcome";
import ParentLogin from "./pages/ParentLogin";
import ParentHome from "./pages/ParentHome";
import ParentAttendance from "./pages/ParentAttendance";
import ParentUpdates from "./pages/ParentUpdates";
import ParentProfile from "./pages/ParentProfile";
import ParentReportAbsence from "./pages/ParentReportAbsence";
import ParentReports from "./pages/ParentReports";
import ParentNotifications from "./pages/ParentNotifications";
import ParentLayout from "./components/parent/ParentLayout";
import ParentLoadingScreen from "./components/parent/ParentLoadingScreen";
import ParentForgotPassword from "./pages/ParentForgotPassword";

import AdminManager from "./pages/AdminManager";
import HomeDashboard from "./pages/HomeDashboard";
import TeacherDashboard from "./pages/TeacherDashboard";
import ParentView from "./pages/ParentView";
import AcceptParentInvite from "./pages/AcceptParentInvite";
import AcceptStaffInvite from "./pages/AcceptStaffInvite";
import StaffInviteManager from "./pages/StaffInviteManager";
import ResetPassword from "./pages/ResetPassword";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import DemoRequests from "./pages/DemoRequests";
import SchoolAdminOnboarding from "./pages/SchoolAdminOnboarding";
import SetupMFA from "./pages/security/SetupMFA";
import AdminMFAChallenge from "./pages/security/AdminMFAChallenge";
import Security from "./pages/legal/Security";
import TrustCenter from "./pages/legal/TrustCenter";
import CookiePolicy from "./pages/legal/CookiePolicy";
import AgreementReviewPage from "./pages/agreements/AgreementReviewPage";
import SchoolSetupWizard from "./pages/setup/SchoolSetupWizard.jsx";
import AgreementViewerPage from "./pages/agreements/AgreementViewerPage.jsx";
import MfaSetupPage from "./pages/security/MfaSetupPage.jsx";
import AgreementVerificationPage from "./pages/agreements/AgreementVerificationPage.jsx";
import ActivateSchoolPage from "./pages/ActivateSchoolPage";

function getLocalParentOnboardingDone(uid) {
  if (!uid) return false;

  try {
    return (
      window.localStorage.getItem(`pp_parent_onboarding_done_${uid}`) === "true"
    );
  } catch {
    return false;
  }
}

function FullScreenLoader({ text = "Preparing PresentPlus..." }) {
  return <ParentLoadingScreen text={text} />;
}

function useAuthGate() {
  const {
    user,
    role,
    profile,
    loading,
    profileLoading,
    authResolved,
    profileResolved,
    requiresMfaChallenge,
  } = useAuth();

  const effectiveRole = useMemo(() => {
    const rawRole = role || profile?.role || null;
    const lower = String(rawRole || "").toLowerCase().trim();

    if (lower === "teacher") return "staff";
    if (lower === "manager") return "admin";

    return lower || null;
  }, [role, profile]);

  const authWaiting = loading || !authResolved;
  const profileWaiting = !!user && (profileLoading || !profileResolved);

  return {
    user,
    role: effectiveRole,
    profile,
    authWaiting,
    profileWaiting,
    requiresMfaSetup:
      (effectiveRole === "admin" || effectiveRole === "super_admin") &&
      profile?.mfaRequired === true &&
      profile?.mfaCompleted !== true,
    requiresMfaChallenge:
      (effectiveRole === "admin" || effectiveRole === "super_admin") &&
      profile?.mfaRequired === true &&
      profile?.mfaCompleted === true
        ? !!requiresMfaChallenge
        : false,
  };
}

function PublicRootRoute() {
  const isNativeApp = Capacitor.isNativePlatform();

  if (isNativeApp) {
    return <Navigate to="/parent-splash" replace />;
  }

  return <LandingPage />;
}



function ProtectedParentRoute({ children }) {
  const { user, role, profile, authWaiting, profileWaiting } = useAuthGate();
  const location = useLocation();

  if (authWaiting || profileWaiting) {
    return <FullScreenLoader text="Loading your parent dashboard..." />;
  }

  if (!user) return <Navigate to="/parent-login" replace />;
  if (role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (role === "admin") return <Navigate to="/admin-manager" replace />;
  if (role === "staff") return <Navigate to="/teacher-dashboard" replace />;
  if (role !== "parent") return <Navigate to="/" replace />;

  const onboardingCompleted =
    profile?.parentOnboardingCompleted === true ||
    profile?.onboardingCompleted === true ||
    getLocalParentOnboardingDone(user?.uid);

  const onboardingRequired =
    profile?.parentOnboardingRequired === true && !onboardingCompleted;

  if (
    onboardingRequired &&
    location.pathname !== "/parent-onboarding"
  ) {
    return <Navigate to="/parent-onboarding" replace />;
  }

  return children;
}

function ProtectedStaffRoute({
  children,
  allowMfaSetup = false,
  allowMfaChallenge = false,
}) {
  const {
    user,
    role,
    authWaiting,
    profileWaiting,
    requiresMfaSetup,
    requiresMfaChallenge,
  } = useAuthGate();

  if (authWaiting || profileWaiting) {
    return <FullScreenLoader text="Loading PresentPlus..." />;
  }

  if (!user) return <Navigate to="/login" replace />;

  if (role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (role === "parent") return <Navigate to="/parent/home" replace />;

  if (role !== "staff" && role !== "admin") {
    return <Navigate to="/" replace />;
  }

  if (requiresMfaSetup && !allowMfaSetup) {
    return <Navigate to="/setup-mfa" replace />;
  }

  if (requiresMfaChallenge && !allowMfaChallenge) {
    return <Navigate to="/admin-mfa" replace />;
  }

  return children;
}

function ProtectedSuperAdminRoute({ children }) {
  const { user, role, authWaiting, profileWaiting } = useAuthGate();

  if (authWaiting || profileWaiting) {
    return <FullScreenLoader text="Loading super admin workspace..." />;
  }

  if (!user) return <Navigate to="/login" replace />;

  if (role !== "super_admin") {
    if (role === "admin") return <Navigate to="/admin-manager" replace />;
    if (role === "parent") return <Navigate to="/parent/home" replace />;
    if (role === "staff") return <Navigate to="/teacher-dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  return children;
}

function PublicStaffLoginRoute() {
  const {
    user,
    role,
    authWaiting,
    profileWaiting,
    requiresMfaSetup,
    requiresMfaChallenge,
  } = useAuthGate();

  if (authWaiting) {
    return <FullScreenLoader text="Preparing PresentPlus..." />;
  }

  if (!user) return <Login />;

  if (profileWaiting && !role) {
    return <FullScreenLoader text="Signing you in..." />;
  }

  if (requiresMfaSetup) {
    return <Navigate to="/setup-mfa" replace />;
  }

  if (requiresMfaChallenge) {
    return <Navigate to="/admin-mfa" replace />;
  }

  if (role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (role === "parent") return <Navigate to="/parent/home" replace />;
  if (role === "admin") return <Navigate to="/admin-manager" replace />;
  if (role === "staff") return <Navigate to="/teacher-dashboard" replace />;

  return <Navigate to="/" replace />;
}

function ParentLoginRoute() {
  const { user, role, profile, authWaiting, profileWaiting } = useAuthGate();
  const [targetPath, setTargetPath] = React.useState(null);

  useEffect(() => {
    let alive = true;

    async function resolveTarget() {
      if (authWaiting) return;

      if (!user) {
        if (alive) setTargetPath(null);
        return;
      }

      if (profileWaiting && !role) return;

      if (role === "super_admin") {
        if (alive) setTargetPath("/super-admin");
        return;
      }

      if (role === "admin") {
        if (alive) setTargetPath("/admin-manager");
        return;
      }

      if (role === "staff") {
        if (alive) setTargetPath("/teacher-dashboard");
        return;
      }

      if (role !== "parent") {
        if (alive) setTargetPath(null);
        return;
      }

      const onboardingCompleted =
        profile?.parentOnboardingCompleted === true ||
        profile?.onboardingCompleted === true ||
        getLocalParentOnboardingDone(user?.uid);

      const onboardingRequired =
        profile?.parentOnboardingRequired === true && !onboardingCompleted;

      if (!alive) return;

      setTargetPath(onboardingRequired ? "/parent-onboarding" : "/parent/home");
    }

    resolveTarget();

    return () => {
      alive = false;
    };
  }, [user, role, profile, authWaiting, profileWaiting]);

  if (authWaiting) return <FullScreenLoader text="Preparing PresentPlus..." />;
  if (!user) return <ParentLogin />;

  if (profileWaiting && !role) {
    return <FullScreenLoader text="Preparing your parent account..." />;
  }

  if (targetPath) return <Navigate to={targetPath} replace />;

  return <ParentLogin />;
}


function NativeDeepLinkBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let listenerHandle = null;
    let alive = true;

    function routeIncomingUrl(rawUrl) {
      if (!rawUrl) return;

      try {
        const parsed = new URL(rawUrl);
        let pathname = parsed.pathname || "/";

        // Custom-scheme URLs place the first path segment in the hostname.
        if (parsed.protocol === "presentplus:") {
          const hostPart = parsed.hostname ? `/${parsed.hostname}` : "";
          pathname = `${hostPart}${parsed.pathname || ""}` || "/parent-login";
        }

        const allowedPaths = new Set([
          "/accept-parent-invite",
          "/accept-parent",
          "/parent-invite",
          "/parent-setup",
          "/parent-login",
          "/parent-onboarding",
        ]);

        if (!allowedPaths.has(pathname)) return;

        navigate(`${pathname}${parsed.search || ""}`, { replace: true });
      } catch (err) {
        console.warn("PresentPlus deep link could not be opened:", err);
      }
    }

    CapacitorApp.getLaunchUrl()
      .then((result) => {
        if (alive && result?.url) routeIncomingUrl(result.url);
      })
      .catch(() => {});

    CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      routeIncomingUrl(url);
    })
      .then((handle) => {
        listenerHandle = handle;
      })
      .catch((err) => {
        console.warn("PresentPlus app link listener could not start:", err);
      });

    return () => {
      alive = false;
      listenerHandle?.remove?.();
    };
  }, [navigate]);

  return null;
}

function AppShell() {
  const isNativeApp = Capacitor.isNativePlatform();

  useEffect(() => {
    setupNativeStatusBar();

    if (isNativeApp) {
      const timer = setTimeout(() => {
        SplashScreen.hide().catch(() => {});
      }, 900);

      return () => clearTimeout(timer);
    }
  }, [isNativeApp]);

  return (
    <Routes>
      <Route path="/" element={<PublicRootRoute />} />

      {/* Web alias so /splash-screen does not fall into wildcard */}
      <Route path="/splash-screen" element={<ParentSplash />} />

      <Route path="/login" element={<PublicStaffLoginRoute />} />

      <Route
        path="/setup-mfa"
        element={
          <ProtectedStaffRoute allowMfaSetup>
            <SetupMFA />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/admin-mfa"
        element={
          <ProtectedStaffRoute allowMfaChallenge>
            <AdminMFAChallenge />
          </ProtectedStaffRoute>
        }
      />

      <Route path="/parent-entry" element={<Navigate to="/parent-splash" replace />} />
      <Route path="/parent-splash" element={<ParentSplash />} />
      <Route path="/parent-welcome" element={<ParentWelcome />} />
      <Route path="/parent-login" element={<ParentLoginRoute />} />
      <Route path="/parent-forgot-password" element={<ParentForgotPassword />} />

      <Route
        path="/parent-onboarding"
        element={
          <ProtectedParentRoute>
            <ParentOnboarding />
          </ProtectedParentRoute>
        }
      />

      <Route
        path="/parent"
        element={
          <ProtectedParentRoute>
            <ParentLayout />
          </ProtectedParentRoute>
        }
      >
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<ParentHome />} />
        <Route path="attendance" element={<ParentAttendance />} />
        <Route path="updates" element={<ParentUpdates />} />
        <Route path="notifications" element={<ParentNotifications />} />
        <Route path="profile" element={<ParentProfile />} />
        <Route path="report-absence" element={<ParentReportAbsence />} />
        <Route path="reports" element={<ParentReports />} />
      </Route>

      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/data-protection" element={<DataProtection />} />
      <Route path="/security" element={<Security />} />
      <Route path="/trust" element={<TrustCenter />} />
      <Route path="/cookies" element={<CookiePolicy />} />

      <Route
        path="/super-admin"
        element={
          <ProtectedSuperAdminRoute>
            <SuperAdminDashboard />
          </ProtectedSuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/demo-requests"
        element={
          <ProtectedSuperAdminRoute>
            <DemoRequests />
          </ProtectedSuperAdminRoute>
        }
      />

      <Route
        path="/school-admin-onboarding"
        element={
          <ProtectedStaffRoute>
            <SchoolAdminOnboarding />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedStaffRoute>
            <AdminManager />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/admin-manager"
        element={
          <ProtectedStaffRoute>
            <AdminManager />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedStaffRoute>
            <HomeDashboard />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/home"
        element={
          <ProtectedStaffRoute>
            <TeacherDashboard />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/teacher-dashboard"
        element={
          <ProtectedStaffRoute>
            <TeacherDashboard />
          </ProtectedStaffRoute>
        }
      />

      <Route
        path="/parent-view"
        element={
          <ProtectedStaffRoute>
            <ParentView />
          </ProtectedStaffRoute>
        }
      />

      {/* Parent invite routes: keep all aliases so old emails never fall to landing */}
      <Route path="/accept-parent-invite" element={<AcceptParentInvite />} />
      <Route path="/accept-parent" element={<AcceptParentInvite />} />
      <Route path="/parent-invite" element={<AcceptParentInvite />} />
      <Route path="/parent-setup" element={<AcceptParentInvite />} />
      <Route path="/accept-staff-invite" element={<AcceptStaffInvite />} />
      <Route path="/staff-invites" element={<StaffInviteManager />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/agreements/:token" element={<AgreementReviewPage />} />
      <Route path="/setup" element={<SchoolSetupWizard />} />
      <Route path="/agreement-viewer" element={<AgreementViewerPage />} />
      <Route path="/mfa-setup" element={<MfaSetupPage />} />
      <Route path="/verify-agreement" element={<AgreementVerificationPage />} />
      <Route path="/activate-school" element={<ActivateSchoolPage />} />

      <Route
        path="*"
        element={
          isNativeApp ? <Navigate to="/parent-splash" replace /> : <Navigate to="/" replace />
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <NativeDeepLinkBridge />
      <AppShell />
    </Router>
  );
}